<template>
  <v-card>
    <div v-if="isLoading" class="dialog-spinner py-5">
      <v-progress-circular indeterminate size="50" color="primary"></v-progress-circular>
    </div>

    <template v-else-if="item">
      <v-toolbar flat dark color="transparent" style="z-index: 1; width: 100%;" absolute>
        <v-btn v-if="$vuetify.breakpoint.xsOnly" icon @click.native="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <div class="flex-grow-1"></div>
        <v-btn icon @click="$emit('toggleMode')">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </v-toolbar>
      <div class="blue text-center pa-4 white--text" style="position: relative">
        <v-avatar size="158" color="grey lighten-4" class="mb-2">
          <img
            style="border: 4px solid white"
            :src="item.image ? item.image.contentUrl + '?x-request=html' : require('@/assets/no-img.svg')"
            :alt="item.firstName"
          />
        </v-avatar>

        <div class="headline">{{ item.firstName }} {{ item.lastName }}</div>
      </div>
      <v-alert v-if="notAllowedUser" color="red" type="error" class="mb-0 rounded-0">
        {{ $t('message.not_allowed') }}
      </v-alert>

      <v-card-text class="pa-0">
        <v-tabs v-model="selectedTab" centered background-color="grey lighten-4">
          <v-tabs-slider color="warning"></v-tabs-slider>
          <v-tab href="#tab-user" active>
            {{ $t('message.data') }}
          </v-tab>
          <v-tab v-if="!notAllowedUser" href="#tab-credit">
            {{ $t('message.credit') }}
          </v-tab>

          <v-tab-item key="tab-user" value="tab-user">
            <v-list two-line>
              <v-list-item>
                <v-list-item-action>
                  <v-icon color="grey">mdi-account</v-icon>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title v-if="item.username">{{ item.username }}</v-list-item-title>
                  <v-list-item-title v-else> <v-icon>mdi-lock</v-icon> </v-list-item-title>

                  <v-list-item-subtitle>{{ $t('message.user_name') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-divider inset></v-divider>

              <v-list-item>
                <v-list-item-action>
                  <v-icon color="grey">mdi-email</v-icon>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title v-if="item.email">{{ item.email }}</v-list-item-title>
                  <v-list-item-title v-else> <v-icon>mdi-lock</v-icon> </v-list-item-title>

                  <v-list-item-subtitle>{{ $t('message.email') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-divider inset></v-divider>

              <v-list-item>
                <v-list-item-action>
                  <v-icon color="grey">mdi-translate</v-icon>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title>{{
                    item.locale === 'es' ? $t('message.spanish') : $t('message.english')
                  }}</v-list-item-title>
                  <v-list-item-subtitle>{{ $t('message.language') }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-divider inset></v-divider>
              <v-list-item class="d-flex align-center">
                <v-list-item-action>
                  <v-icon color="grey">mdi-dialpad</v-icon>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title v-if="notAllowedUser">
                    <v-icon>mdi-lock</v-icon>
                  </v-list-item-title>

                  <v-list-item-title v-else-if="item.pin">
                    {{ item.pin }}
                  </v-list-item-title>
                  <v-list-item-title v-else class="grey--text">No Data</v-list-item-title>

                  <v-list-item-subtitle>PIN</v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action v-if="isShipowner || isCaptain">
                  <v-btn color="blue" dark depressed @click="pinDialog = true">
                    {{ $t('message.change_pin') }}
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <v-divider></v-divider>

            <template v-if="item.roleInOrganizations && item.roleInOrganizations.length > 0">
              <v-list subheader two-line>
                <v-subheader>{{ $t('message.role_in_organizations') }}</v-subheader>

                <template v-for="(roleInOrganization, index) in item.roleInOrganizations">
                  <v-divider v-if="index !== 0" :key="index" inset></v-divider>
                  <v-list-item :key="roleInOrganization['@id']">
                    <v-list-item-avatar>
                      <img
                        :src="
                          roleInOrganization.organization.image
                            ? roleInOrganization.organization.image.contentUrl + '?x-request=html'
                            : require('@/assets/no-img.svg')
                        "
                      />
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title>{{ roleInOrganization.organization.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{
                        $t('message.' + roleInOrganization.organizationRole.name)
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-list>
            </template>

            <template v-if="item.roleInVessels && item.roleInVessels.length > 0">
              <v-divider></v-divider>
              <v-list subheader two-line>
                <v-subheader>{{ $t('message.role_in_vessels') }}</v-subheader>

                <template v-for="(roleInVessel, index) in item.roleInVessels">
                  <v-divider v-if="index !== 0" :key="index" inset></v-divider>
                  <v-list-item :key="roleInVessel['@id']">
                    <v-list-item-avatar>
                      <img
                        :src="
                          roleInVessel.vessel.image
                            ? roleInVessel.vessel.image.contentUrl + '?x-request=html'
                            : require('@/assets/no-img.svg')
                        "
                      />
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title>{{ roleInVessel.vessel.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ $t('message.' + roleInVessel.vesselRole.name) }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-list>
            </template>

            <v-list v-if="hotspotUsers" subheader two-line>
              <v-divider></v-divider>
              <v-subheader>Hotspots</v-subheader>
              <v-expansion-panels hover accordion flat>
                <v-expansion-panel
                  v-for="(hotspotUser, index) in hotspotUsers"
                  :key="'panel-' + index"
                  :style="index !== hotspotUsers.length - 1 ? 'border-bottom: 1px solid rgba(0, 0, 0, 0.12)' : ''"
                >
                  <v-expansion-panel-header>
                    <v-row no-gutters class="align-center">
                      <v-col>
                        <v-icon left>mdi-ferry</v-icon>
                        <span>{{ hotspotUser.siteVpn.site.name }}</span>
                      </v-col>
                      <v-col v-if="isShipowner || isCaptain" class="text-right">
                        <v-switch
                          v-model="hotspotUser.enabled"
                          label="Enabled"
                          :loading="updateHotspotEnabledLoading"
                          @change="toggleHotspotEnabled(hotspotUser)"
                        />
                      </v-col>
                    </v-row>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content>
                    <v-divider inset></v-divider>
                    <v-list-item class="d-flex align-center">
                      <v-list-item-action>
                        <v-icon color="grey">mdi-account</v-icon>
                      </v-list-item-action>

                      <v-list-item-content>
                        <v-list-item-title v-if="notAllowedUser">
                          <v-icon>mdi-lock</v-icon>
                        </v-list-item-title>

                        <v-list-item-title v-else-if="hotspotUser.username">
                          {{ hotspotUser.username }}
                        </v-list-item-title>
                        <v-list-item-title v-else class="grey--text">No Data</v-list-item-title>

                        <v-list-item-subtitle> {{ $t('message.username') }} </v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action v-if="isShipowner || isCaptain"> </v-list-item-action>
                    </v-list-item>

                    <v-divider inset></v-divider>
                    <v-list-item class="d-flex align-center">
                      <v-list-item-action>
                        <v-icon color="grey">mdi-dialpad</v-icon>
                      </v-list-item-action>

                      <v-list-item-content>
                        <v-list-item-title v-if="notAllowedUser">
                          <v-icon>mdi-lock</v-icon>
                        </v-list-item-title>

                        <v-list-item-title v-else-if="hotspotUser.password">
                          {{ hotspotUser.password }}
                        </v-list-item-title>
                        <v-list-item-title v-else class="grey--text">No Data</v-list-item-title>

                        <v-list-item-subtitle> {{ $t('message.password') }} </v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action v-if="isShipowner || isCaptain">
                        <v-btn color="blue" dark depressed @click="openChangePasswordDialog(hotspotUser)">
                          {{ $t('message.change_password') }}
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>

                    <v-divider inset></v-divider>

                    <v-list-item class="d-flex align-center">
                      <v-list-item-action>
                        <v-icon color="grey">mdi-wallet</v-icon>
                      </v-list-item-action>

                      <v-list-item-content>
                        <v-list-item-title v-if="notAllowedUser">
                          <v-icon>mdi-lock</v-icon>
                        </v-list-item-title>

                        <v-list-item-title v-else-if="hotspotUser.credit !== null && hotspotUser.credit !== undefined">
                          {{ formatHotspotUserCredit(hotspotUser) }}
                        </v-list-item-title>
                        <v-list-item-title v-else class="grey--text">No Data</v-list-item-title>

                        <v-list-item-subtitle>{{ $t('message.hotspot_credits') }}</v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action v-if="(isCaptain || isShipowner) && hotspotUser.siteVpn.hotspotReseller">
                        <v-btn color="blue" dark depressed @click="openCreditDialog(hotspotUser)">
                          Add Credit
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-list>
          </v-tab-item>
          <v-tab-item key="tab-credit" value="tab-credit">
            <div v-if="hotspotCreditActivitiesLoading" class="pa-5 text-center">
              <v-progress-circular indeterminate size="25" color="primary"></v-progress-circular>
            </div>
            <div
              v-else-if="
                !hotspotCreditActivitiesLoading && hotspotCreditActivities && hotspotCreditActivities.length > 0
              "
            >
              <v-list two-line>
                <template v-for="(hotspotCreditActivity, index) in hotspotCreditActivities">
                  <v-divider v-if="index > 0" :key="hotspotCreditActivity.name + index.toString()"></v-divider>
                  <v-list-item :key="hotspotCreditActivity.name">
                    <v-list-item-icon>
                      <v-icon>mdi-credit-card</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title :style="{ color: hotspotCreditActivity.amount > 0 ? 'green' : 'red' }">
                        {{ formatHotspotCreditActivity(hotspotCreditActivity) }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ hotspotCreditActivity.createdAt | moment('DD/MM/YYYY') }}
                        - by
                        <template v-if="hotspotCreditActivity.createdBy">
                          {{ hotspotCreditActivity.createdBy.firstName }}
                          {{ hotspotCreditActivity.createdBy.lastName }}
                        </template>
                        <template v-else>
                          {{ hotspotCreditActivity.hotspotUser.fullName }}
                        </template>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <div class="text-center mb-6">
                  <div class="mt-3 mb-2">
                    Showing <strong>{{ doneOperations.length }}</strong> of
                    <strong>{{ doneOperationsTotalItems }}</strong> operations
                  </div>
                  <v-btn
                    v-if="doneOperationsTotalItems > doneOperations.length"
                    small
                    depressed
                    color="primary"
                    @click="loadMoreHotspotCreditActivities"
                    >Load more</v-btn
                  >
                </div>
              </v-list>
            </div>
            <div
              v-else
              style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%;"
            >
              <v-icon color="grey lighten-2" size="150px">mdi-file-outline</v-icon>
              <h1 class="headline">No credit history</h1>
              <p class="subheading">This user has no credit history yet.</p>
            </div>
          </v-tab-item>
        </v-tabs>
      </v-card-text>

      <v-divider class="hidden-xs-only"></v-divider>
      <v-card-actions v-if="!isLoading" class="hidden-xs-only">
        <div class="flex-grow-1"></div>
        <v-btn color="grey" text @click="$emit('close')">{{ $t('message.close') }}</v-btn>
      </v-card-actions>
    </template>
    <v-dialog v-model="creditDialog" max-width="380">
      <v-card>
        <v-card-title>Add Credit</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="hotspotCredit"
            label="Credit"
            :suffix="creditType === 'time' ? 'Minutes' : 'MB'"
            type="number"
            clearable
            required
            :rules="creditRules"
            :loading="updateHotspotCreditLoading"
          >
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="updateHotspotCreditLoading" color="grey darken-1" text @click="creditDialog = false"
            >Close</v-btn
          >
          <v-btn
            :disabled="updateHotspotCreditLoading"
            color="blue darken-1"
            :loading="updateHotspotCreditLoading"
            type="submit"
            text
            @click="updateCredit"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="pinDialog" max-width="380">
      <v-card>
        <v-card-title>Change PIN</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="pin"
            label="PIN"
            hint="The user PIN"
            clearable
            required
            :rules="pinRules"
            type="number"
            append-icon="cached"
            :loading="updatePinLoading"
            :error-messages="isInvalid('pin') ? violations.pin : []"
            @click:append="generatePin"
          >
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="updatePinLoading" color="grey darken-1" text @click="pinDialog = false">Close</v-btn>
          <v-btn
            :disabled="updatePinLoading"
            :loading="updatePinLoading"
            color="blue darken-1"
            text
            type="submit"
            @click="updatePin"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" max-width="380">
      <v-card>
        <v-card-title>
          {{ hotspotUserKey === 'password' ? $t('message.change_password') : $t('message.change_username') }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="hotspotUserValue"
            :label="hotspotUserKey"
            :hint="`The user ${hotspotUserKey}`"
            clearable
            required
            :rules="hotspotUserRules"
            type="text"
            :loading="updateHotspotLoading"
            :error-messages="isInvalid(hotspotUserKey) ? violations[hotspotUserKey] : []"
          >
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="updateHotspotLoading" color="grey darken-1" text @click="dialog = false">Close</v-btn>
          <v-btn
            :disabled="updateHotspotLoading || !hotspotUserValue"
            :loading="updateHotspotLoading"
            color="blue darken-1"
            text
            type="submit"
            @click="updateHotspotUser(hotspotUserKey, hotspotUserValue)"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="serviceDialog" max-width="380">
      <v-card>
        <v-card-title>Hotspot Service</v-card-title>
        <v-card-text>
          <v-select
            v-model="hotspotService"
            label="Hotspot Service"
            :items="hotspotServices"
            item-text="srvname"
            item-value="srvid"
            hint="The Hotspot Service"
            required
            :loading="hotspotServicesLoading"
            :rules="hotspotServiceRules"
            type="text"
            :error-messages="isInvalid('hotspotService') ? violations.hotspotService : []"
          >
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="updateHotspotLoading" color="grey darken-1" text @click="serviceDialog = false"
            >Close</v-btn
          >
          <v-btn
            :disabled="updateHotspotLoading"
            :loading="hotspotServicesLoading"
            color="blue darken-1"
            text
            type="submit"
            @click="updateHotspotService"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import fetch from '@/utils/fetch'

export default {
  name: 'UserDialogShow',

  props: {
    user: {
      type: String,
      required: true
    },
    vessel: {
      type: Object,
      required: false,
      default: null
    },
    organization: {
      type: Object,
      required: false,
      default: null
    }
  },
  data: () => ({
    item: {},
    hotspotUserKey: null,
    dialog: false,
    selectedUnit: null,
    hotspotUserValue: null,
    hotspotCreditActivitiesPagination: {
      page: 1,
      itemsPerPage: 25
    },
    hotspotUsers: [],
    hotspotUserToUpdate: null,
    hotspotServices: [],
    hotspotService: null,
    creditDialog: false,
    creditType: null,
    hotspotUsername: null,
    updateHotspotLoading: false,
    hotspotUserRules: [
      v => {
        return !!v || 'The value is required'
      }
    ],
    pinDialog: false,
    serviceDialog: false,
    selectedTab: null,
    violations: {},
    updatePinLoading: false,
    hotspotServicesLoading: false,
    updateHotspotCreditLoading: false,
    hotspotCreditActivitiesLoading: false,
    doneOperationsView: null,
    doneOperationsTotalItems: 0,
    doneOperations: null,
    hotspotCreditActivities: [],
    isLoading: false,
    getUserLoading: false,
    isShipowner: false,
    isCaptain: false,
    loading: false,
    hotspotCredit: null,
    notAllowedUser: false,
    pin: null,
    updateHotspotEnabledLoading: false,
    creditRules: [
      v => {
        return !!v || 'The credit is required'
      }
    ],
    pinRules: [
      v => {
        return !!v || 'The PIN is required'
      }
    ],
    hotspotServiceRules: [
      v => {
        return !!v || 'The Service is required'
      }
    ]
  }),

  computed: {
    ...mapGetters({
      error: 'user/show/error'
    })
  },
  watch: {
    selectedTab() {
      if (this.selectedTab === 'tab-credit') {
        this.fetchHotspotCreditActivities()
      }
    },
    serviceDialog() {
      if (this.serviceDialog) {
        this.hotspotService = this.hotspotServiceObject(this.item.hotspotService)
      }
    }
  },
  beforeDestroy() {
    this.reset()
  },
  mounted() {
    this.getUser()
    this.pin = this.item.pin

    if (this.organization) {
      this.isShipowner = this.$security.isGranted(this.organization['@id'], 'ROLE_SHIPOWNER')
    }
    if (this.vessel) {
      this.isShipowner = this.$security.isGranted(this.vessel.organization['@id'], 'ROLE_SHIPOWNER')
      this.isCaptain = this.$security.isGranted(this.vessel['@id'], 'ROLE_CAPTAIN')
    }
  },
  methods: {
    ...mapActions({
      reset: 'user/show/reset',
      retrieve: 'user/show/retrieve',
      getRoleInVesselsPage: 'roleinvessel/list/default',
      getRoleInOrganizationsPage: 'roleinorganization/list/default'
    }),
    openChangePasswordDialog(hotspotUser) {
      this.hotspotUserKey = 'password'
      this.dialog = true
      this.hotspotUserToUpdate = hotspotUser
    },
    openChangeUsernameDialog(hotspotUser) {
      this.hotspotUserKey = 'username'
      this.dialog = true
      this.hotspotUserToUpdate = hotspotUser
    },
    openCreditDialog(hotspotUser) {
      this.creditDialog = true
      this.creditType = hotspotUser.creditType
      this.hotspotUsername = hotspotUser.username
    },
    formatHotspotUserCredit(hotspotUser) {
      if (hotspotUser.creditType === 'time') {
        const totalMinutes = Math.floor(hotspotUser.credit / 60)
        if (totalMinutes >= 180) {
          const hours = Math.floor(totalMinutes / 60)
          const minutes = totalMinutes % 60
          return minutes > 0
            ? `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
            : `${hours} ${hours === 1 ? 'hour' : 'hours'}`
        } else {
          return `${totalMinutes} minutes`
        }
      } else if (hotspotUser.creditType === 'volume') {
        const megabytes = hotspotUser.credit / (1024 * 1024)
        if (megabytes >= 1024) {
          const gigabytes = (megabytes / 1024).toFixed(2)
          return `${gigabytes} GB`
        } else {
          return `${megabytes.toFixed(2)} MB`
        }
      } else {
        return `${hotspotUser.credit}`
      }
    },
    formatHotspotCreditActivity(hotspotCreditActivity) {
      if (hotspotCreditActivity.type === 'time') {
        const totalMinutes = Math.floor(hotspotCreditActivity.amount / 60)
        if (totalMinutes >= 180) {
          const hours = Math.floor(totalMinutes / 60)
          const minutes = totalMinutes % 60
          return minutes > 0
            ? `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
            : `${hours} ${hours === 1 ? 'hour' : 'hours'}`
        } else {
          return `${totalMinutes} minutes`
        }
      } else if (hotspotCreditActivity.type === 'volume') {
        const megabytes = hotspotCreditActivity.amount / (1024 * 1024)
        const absoluteMegabytes = Math.abs(megabytes)
        if (absoluteMegabytes >= 1024) {
          const gigabytes = (absoluteMegabytes / 1024).toFixed(2)
          return `${megabytes < 0 ? '-' : ''}${gigabytes} GB`
        } else {
          return `${megabytes.toFixed(2)} MB`
        }
      } else {
        return `${hotspotCreditActivity.amount}`
      }
    },
    loadMoreHotspotCreditActivities() {
      this.hotspotCreditActivitiesPagination.itemsPerPage *= 2
      this.fetchHotspotCreditActivities()
    },
    hotspotServiceObject(id) {
      let hotspotServiceObject = ''
      this.hotspotServices.forEach(service => {
        if (service.srvid == id) {
          hotspotServiceObject = service
        }
      })
      return hotspotServiceObject
    },
    generatePin() {
      let max = 9999999999
      let min = 1000000000

      this.pin = Math.floor(Math.random() * (max - min)) + min
      this.pin = this.pin.toString()
    },
    getUser() {
      this.isLoading = true
      fetch(this.user)
        .then(response => response.json())
        .then(data => {
          this.item = data
          this.isLoading = false
        })
        .catch(e => {
          if (e.message === 'Access Denied.') {
            this.notAllowedUser = true
          }
          console.log(e)
          this.isLoading = false
        })
        .then(() => {
          this.getHotspotUser()
        })
    },
    getHotspotUser() {
      this.isLoading = true
      fetch('hotspot_users?user=' + this.item.id + '&radius=true')
        .then(response => response.json())
        .then(data => {
          this.hotspotUsers = data['hydra:member']
        })
        .catch(e => {
          console.log(e)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    isInvalid(key) {
      return Object.keys(this.violations).length > 0 && this.violations[key]
    },
    updatePin() {
      this.updatePinLoading = true

      fetch(this.item['@id'], {
        method: 'PUT',
        body: JSON.stringify({ pin: this.pin })
      })
        .then(response => response.json())
        .then(() => {
          this.updatePinLoading = false
          this.pinDialog = false
          this.getUser()
        })
        .catch(e => {
          this.violations = e.errors
          this.updatePinLoading = false
        })
    },
    updateHotspotUser(key, value) {
      this.updateHotspotLoading = true
      if (key === 'username') {
        value = value.toLowerCase()
      }
      fetch(this.hotspotUserToUpdate['@id'], {
        method: 'PUT',
        body: JSON.stringify({ [key]: value })
      })
        .then(response => response.json())
        .catch(e => {
          this.violations = e.errors
          this.updateHotspotLoading = false
        })
        .finally(() => {
          this.hotspotUserValue = null
          this.updateHotspotLoading = false
          this.dialog = false
          this.getUser()
        })
    },
    megabytesToBytes(megaBytes) {
      return megaBytes * 1048576
    },
    updateCredit() {
      this.updateHotspotCreditLoading = true
      let credit = null
      if (this.creditType === 'volume') {
        credit = this.megabytesToBytes(this.hotspotCredit)
      } else {
        credit = this.hotspotCredit * 60
      }
      fetch('/radius_manager_users/' + this.hotspotUsername + '/add-credit', {
        method: 'PUT',
        body: JSON.stringify({ hotspotCredit: credit })
      })
        .then(response => response.json())
        .then(() => {
          this.creditDialog = false
          this.updateHotspotCreditLoading = false
          this.getUser()
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
          this.creditDialog = false
        })
        .finally(() => {
          this.hotspotCredit = null
        })
    },
    updateHotspotService() {
      this.hotspotServicesLoading = true
      fetch('/radius_manager_users/' + this.item.username + '/service', {
        method: 'PUT',
        body: JSON.stringify({ hotspotService: this.hotspotService })
      })
        .then(response => response.json())
        .then(() => {
          this.hotspotServicesLoading = false
          this.serviceDialog = false
          this.getUser()
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
          this.hotspotServicesLoading = false
        })
    },
    getHotspotServices() {
      this.hotspotServicesLoading = true
      fetch('/radius-manager-services?selectable=true', {
        method: 'GET',
        headers: new Headers({ 'Content-Type': 'application/ld+json' })
      })
        .then(response => response.json())
        .then(data => {
          this.hotspotServices = data
          this.hotspotServicesLoading = false
        })
        .catch(() => {
          this.hotspotServicesLoading = false
        })
    },
    fetchHotspotCreditActivities() {
      this.hotspotCreditActivitiesLoading = true
      fetch(
        '/hotspot_credit_activities?user.id=' +
          this.item.id +
          '&page=' +
          this.hotspotCreditActivitiesPagination.page +
          '&itemsPerPage=' +
          this.hotspotCreditActivitiesPagination.itemsPerPage +
          '&order[createdAt]=desc' +
          '&radius=true' +
          '&exists[hotspotUser]=true' +
          '&exists[hotspotUser.user]=true',
        {
          method: 'GET'
        }
      )
        .then(response => response.json())
        .then(data => {
          this.hotspotCreditActivities = data['hydra:member']
          this.doneOperations = data['hydra:member']
          this.doneOperationsView = data['hydra:view']
          this.doneOperationsTotalItems = data['hydra:totalItems']
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
          this.error = e
        })
        .finally(() => {
          this.hotspotCreditActivitiesLoading = false
        })
    },

    toggleHotspotEnabled(hotspotUser) {
      this.updateHotspotEnabledLoading = true
      fetch('/radius_manager_users/' + hotspotUser.username + '/enabled', {
        method: 'PUT',
        body: JSON.stringify({ enabled: hotspotUser.enabled })
      })
        .then(response => response.json())
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
        .finally(() => {
          this.getUser()
          this.updateHotspotEnabledLoading = false
        })
    }
  }
}
</script>
<style scoped lang="scss">
::v-deep .v-expansion-panel-content__wrap {
  padding: 0 !important;
}
</style>
